import { Card } from '../../../molecules/Card/Card';
import type { KPIMetric } from '../../../molecules/KPI/KPI';
import { KPIText } from '../../../molecules/KPIText/KPIText';
import { cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

/**
 * The four `Size=sm` figures across the top of Overview (972:20768–972:20771),
 * and the first four widgets of a custom report (994:5153).
 *
 * Three of the four are ordinary metrics over one row set, so they are one
 * component parameterised three ways (`TotalMentionsCard`,
 * `TotalUniqueAudienceCard`, `NegativeConversationCard`). The fourth is not:
 * share of voice needs two row sets, ours and the category's — see
 * `ShareOfVoiceKpiCard`.
 */

export type SummaryCardCopy = {
  title: string;
  helpText: string;
  helpSupportingText: string;
};

export type MetricSummaryView = {
  /** The figure, with its delta badge when comparison is on. Its label is unused (the header is the label). */
  kpi: KPIMetric;
  /** Whether any mention is in scope. */
  hasRows: boolean;
};

export type MetricSummaryCardProps = WidgetBlockProps & {
  view?: MetricSummaryView;
};

/**
 * One metric, one figure, one delta badge — the card's own header is the label,
 * which is why the body is a bare `KPIText` rather than a `KPI` row.
 */
export function MetricSummaryCard({
  copy,
  status,
  view,
  span = 3,
  chrome,
  onRetry,
}: MetricSummaryCardProps & { copy: SummaryCardCopy }) {
  const hasData = view !== undefined && view.hasRows;
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      size="sm"
      {...cardState(status, hasData, onRetry)}
      title={copy.title}
      helpText={copy.helpText}
      helpSupportingText={copy.helpSupportingText}
      kpi={
        view && (
          <KPIText tone={view.kpi.tone} badge={view.kpi.badge}>
            {view.kpi.value}
          </KPIText>
        )
      }
    />
  );
}
